import React, { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import axios from "axios"; // Import axios

// List of dental services for the appointment form
const dentalServices = [
  "General Check-up",
  "Teeth Cleaning",
  "Tooth Extraction",
  "Fillings",
  "Root Canal",
  "Dental Implants",
  "Teeth Whitening",
  "Orthodontics",
  "Dentures",
  "Emergency Dental Care",
  "Pediatric Dentistry",
  "Cosmetic Consultation",
];

// List of dentists for the appointment form
const dentists = [
  {
    id: 1,
    name: "Dr. Michael Chen",
    specialty: "General & Cosmetic Dentistry",
  },
  {
    id: 2,
    name: "Dr. Sarah Johnson",
    specialty: "Orthodontics",
  },
  {
    id: 3,
    name: "Dr. James Wilson",
    specialty: "Pediatric Dentistry",
  },
  {
    id: 4,
    name: "Dr. Emily Rodriguez",
    specialty: "Cosmetic Dentistry",
  },
];

const Appointment: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    date: "",
    time: "",
    service: "",
    dentist: "",
    isNewPatient: "yes",
    message: "",
  });

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(
    "Please fill out all required fields."
  );

  const [headerRef, headerInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const [formRef, formInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [infoRef, infoInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // --- MODIFIED HANDLESUBMIT FUNCTION ---
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Client-side validation
    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.email ||
      !formData.phone ||
      !formData.date ||
      !formData.time ||
      !formData.service
    ) {
      setErrorMessage("Please fill out all required fields.");
      setFormError(true);
      setFormSubmitted(false); // Hide success message if it was shown
      return;
    }

    setFormError(false); // Reset error state before API call

    try {
      // Send form data to the backend API
      const response = await axios.post(
        "http://localhost:5000/api/appointments", // Your backend API endpoint
        formData
      );

      console.log("Appointment created successfully:", response.data);

      // Reset form on successful submission
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        date: "",
        time: "",
        service: "",
        dentist: "",
        isNewPatient: "yes",
        message: "",
      });

      // Show success message
      setFormSubmitted(true);

      // Hide success message after 5 seconds
      setTimeout(() => {
        setFormSubmitted(false);
      }, 5000);
    } catch (error) {
      console.error("Error submitting form:", error);
      // Set an error message to display to the user
      setErrorMessage("Sorry, something went wrong. Please try again later.");
      setFormError(true);
      setFormSubmitted(false); // Ensure success message is hidden
    }
  };
  // --- END OF MODIFICATION ---

  // Get current date for min date in the date picker
  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="pt-24 md:pt-28">
      {/* Header Section */}
      <section
        ref={headerRef}
        className="bg-gradient-to-r from-dark-blue to-primary text-white py-16 md:py-24"
      >
        <div className="container-custom text-center">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="text-4xl md:text-5xl font-bold mb-4"
          >
            Book an Appointment
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={headerInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-xl max-w-3xl mx-auto"
          >
            Schedule your visit using our convenient online booking system.
          </motion.p>
        </div>
      </section>

      {/* Appointment Form and Info Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container-custom">
          <div className="grid grid-cols-1 gap-12">
            {/* Appointment Form */}
            <motion.div
              ref={formRef}
              initial={{ opacity: 0, x: -50 }}
              animate={formInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="bg-white rounded-lg shadow-lg p-8 lg:col-span-2"
            >
              <h2 className="text-2xl font-bold mb-6 text-gray-800">
                Schedule Your Visit
              </h2>

              {formSubmitted && (
                <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-6 flex items-start">
                  <svg
                    className="w-5 h-5 mr-2 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    ></path>
                  </svg>
                  <div>
                    <p className="font-semibold">
                      Appointment Request Received!
                    </p>
                    <p>
                      We'll contact you shortly to confirm your appointment
                      details.
                    </p>
                  </div>
                </div>
              )}

              {formError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 flex items-start">
                  <svg
                    className="w-5 h-5 mr-2 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                  <span>{errorMessage}</span>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Personal Information */}
                  <div>
                    <label
                      htmlFor="firstName"
                      className="block text-gray-700 font-medium mb-2"
                    >
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Your first name"
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="lastName"
                      className="block text-gray-700 font-medium mb-2"
                    >
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Your last name"
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-gray-700 font-medium mb-2"
                    >
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Your email"
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-gray-700 font-medium mb-2"
                    >
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Your phone number"
                      required
                    />
                  </div>

                  {/* Appointment Information */}
                  <div>
                    <label
                      htmlFor="date"
                      className="block text-gray-700 font-medium mb-2"
                    >
                      Preferred Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      id="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      min={today}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="time"
                      className="block text-gray-700 font-medium mb-2"
                    >
                      Preferred Time <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="time"
                      name="time"
                      value={formData.time}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                    >
                      <option value="">Select a time</option>
                      <option value="09:00 AM">09:00 AM</option>
                      <option value="10:00 AM">10:00 AM</option>
                      <option value="11:00 AM">11:00 AM</option>
                      <option value="12:00 PM">12:00 PM</option>
                      <option value="01:00 PM">01:00 PM</option>
                      <option value="02:00 PM">02:00 PM</option>
                      <option value="03:00 PM">03:00 PM</option>
                      <option value="04:00 PM">04:00 PM</option>
                      <option value="05:00 PM">05:00 PM</option>
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="service"
                      className="block text-gray-700 font-medium mb-2"
                    >
                      Service <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                    >
                      <option value="">Select a service</option>
                      {dentalServices.map((service, index) => (
                        <option key={index} value={service}>
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* <div>
                    <label
                      htmlFor="dentist"
                      className="block text-gray-700 font-medium mb-2"
                    >
                      Preferred Dentist
                    </label>
                    <select
                      id="dentist"
                      name="dentist"
                      value={formData.dentist}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="">No preference</option>
                      {dentists.map((dentist) => (
                        <option key={dentist.id} value={dentist.name}>
                          {dentist.name} - {dentist.specialty}
                        </option>
                      ))}
                    </select>
                  </div> */}
                </div>

                {/* Submit Button */}
                <div className="mt-8">
                  <button type="submit" className="btn-primary w-full">
                    Request Appointment
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Find Us Section (Google Maps) */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden container-custom my-16 md:my-24">
        <h2 className="text-2xl font-bold p-6 border-b">Find Us</h2>
        <div className="h-96 w-full">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4361.072863169836!2d72.52809017597923!3d23.006053079185378!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395e85005ae957fb%3A0x1b73799de4fcdc29!2sAnant%20Dental%20Clinic%20%26%20Implant%20Centre!5e1!3m2!1sen!2sin!4v1747306126034!5m2!1sen!2sin"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen={true}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Anant Dental Clinic Location"
          />
        </div>
      </div>
    </div>
  );
};

export default Appointment;
